import { useState, useEffect, useContext, useCallback } from "react";
import { SidebarContext } from "../../../shared/context/SidebarContext";
import { toast } from "react-toastify";
import { Megaphone, KeyRound, RefreshCw, Trash2, Plug, Database, CalendarDays, Activity } from "lucide-react";
import {
    fetchResumo, fetchTokens, conectarToken, revalidarToken, removerToken,
    fetchContas, setContaAtiva, sincronizarConta, fetchPixelsConta,
} from "../services/adsApi";

// Tráfego Pago → Contas de anúncio. Conecta a credencial (uma por plataforma/BM), lista as contas
// que ela enxerga, liga as que entram no BI e dispara/sincroniza. Tudo por empresa.

const PROVIDERS = [
    { key: "meta", label: "Meta Ads", disponivel: true },
    { key: "google", label: "Google Ads", disponivel: false },
];

function formatR$(v) {
    return "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}
function quando(d) {
    return d ? new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
}
function dataBr(d) {
    return d ? new Date(d).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—";
}

function StatusConta({ c }) {
    if (!c.ativo) return <span className="chip-neutral">Fora do BI</span>;
    if (c.sync_em_andamento) return <span className="chip-warning"><span className="h-1.5 w-1.5 rounded-full bg-warning-400 animate-glow-pulse" />Sincronizando</span>;
    if (c.ultimo_erro) return <span className="chip-danger" title={c.ultimo_erro}>Erro no sync</span>;
    if (c.ultimo_sync_ok) return <span className="chip-positive">Sincronizada</span>;
    return <span className="chip-brand">Aguardando 1º sync</span>;
}

export default function AdsContas() {
    const { isOpen } = useContext(SidebarContext);
    const [resumo, setResumo] = useState(null);
    const [tokens, setTokens] = useState([]);
    const [contas, setContas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ provider: "meta", token: "", business_id: "" });
    const [conectando, setConectando] = useState(false);
    const [mostrarForm, setMostrarForm] = useState(false);
    const [busy, setBusy] = useState({}); // id → ação
    const [pixels, setPixels] = useState(null); // { conta, lista }

    const carregar = useCallback(async () => {
        try {
            const [r, t, c] = await Promise.all([fetchResumo(), fetchTokens(), fetchContas()]);
            setResumo(r); setTokens(t); setContas(c);
            if (t.length === 0) setMostrarForm(true);
        } catch {
            toast.error("Erro ao carregar contas de anúncio");
        }
        setLoading(false);
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    // Enquanto alguma conta sincroniza, atualiza a lista a cada 10 s
    useEffect(() => {
        if (!contas.some(c => c.sync_em_andamento)) return;
        const t = setInterval(carregar, 10000);
        return () => clearInterval(t);
    }, [contas, carregar]);

    async function handleConectar(e) {
        e.preventDefault();
        if (!form.token.trim()) return toast.warn("Cole o token gerado no Business Manager.");
        setConectando(true);
        try {
            const r = await conectarToken({ provider: form.provider, token: form.token.trim(), business_id: form.business_id.trim() || undefined });
            toast.success(`Conectado: ${r.usuario?.nome || "token válido"} · ${r.contas.length} conta(s) encontrada(s). Marque as que entram no BI.`);
            setForm({ provider: "meta", token: "", business_id: "" });
            setMostrarForm(false);
            await carregar();
        } catch (err) {
            toast.error(err.response?.data?.message || "Não foi possível conectar o token");
        }
        setConectando(false);
    }

    async function handleRevalidar(id) {
        setBusy(b => ({ ...b, [`t${id}`]: "revalidar" }));
        try {
            const r = await revalidarToken(id);
            toast.success(`Token OK · ${r.contas} conta(s) visíveis`);
            await carregar();
        } catch (err) {
            toast.error(err.response?.data?.message || "Token inválido");
            await carregar();
        }
        setBusy(b => ({ ...b, [`t${id}`]: null }));
    }

    async function handleRemover(id) {
        if (!window.confirm("Remover esta credencial? As contas ficam, mas param de sincronizar até conectar outro token.")) return;
        try { await removerToken(id); toast.success("Credencial removida"); await carregar(); }
        catch { toast.error("Erro ao remover"); }
    }

    async function handleAtivo(c) {
        setBusy(b => ({ ...b, [c.id]: "ativo" }));
        try {
            await setContaAtiva(c.id, !c.ativo);
            setContas(cs => cs.map(x => x.id === c.id ? { ...x, ativo: !c.ativo } : x));
            if (!c.ativo) toast.info("Conta ligada. Clique em Sincronizar pra puxar os dados.");
        } catch { toast.error("Erro ao alterar"); }
        setBusy(b => ({ ...b, [c.id]: null }));
    }

    async function handleSync(c) {
        setBusy(b => ({ ...b, [c.id]: "sync" }));
        setContas(cs => cs.map(x => x.id === c.id ? { ...x, sync_em_andamento: true } : x));
        try {
            const r = await sincronizarConta(c.id, 7);
            if (r.erro) toast.error(`Sync falhou: ${r.erro}`);
            else toast.success(`Sincronizada: ${r.insights} linhas de métricas${typeof r.objetos === "number" ? `, ${r.objetos} objetos` : ""} em ${r.segundos}s`);
        } catch (err) {
            toast.error(err.response?.data?.message || "Erro no sync");
        }
        setBusy(b => ({ ...b, [c.id]: null }));
        await carregar();
    }

    async function handlePixels(c) {
        setBusy(b => ({ ...b, [c.id]: "pixels" }));
        try { setPixels({ conta: c, lista: await fetchPixelsConta(c.id) }); }
        catch (err) { toast.error(err.response?.data?.message || "Erro ao listar pixels"); }
        setBusy(b => ({ ...b, [c.id]: null }));
    }

    const kpis = resumo ? [
        { label: "Contas no BI", value: `${resumo.contas_ativas}/${resumo.contas}`, icon: Plug, helper: "ligadas / visíveis" },
        { label: "Campanhas", value: resumo.campanhas, icon: Megaphone, helper: `${resumo.anuncios} anúncios` },
        { label: "Histórico", value: resumo.insights_desde ? `${dataBr(resumo.insights_desde)} → ${dataBr(resumo.insights_ate)}` : "—", icon: CalendarDays, helper: "métricas diárias no banco", small: true },
        { label: "Último sync", value: quando(resumo.ultimo_sync_ok), icon: Activity, helper: "a cada 15 min pras contas ligadas", small: true },
    ] : [];

    return (
        <div className="min-h-screen text-ink-1 flex relative z-0">
            <div className={`transition-all duration-300 ease-out-expo ${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full max-w-full mb-16 sm:mb-0`}>
                <div className="px-5 sm:px-8 pt-8 pb-12 max-w-[1600px] mx-auto">

                    <header className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 animate-fade-in">
                        <div className="space-y-1">
                            <span className="label-overline text-brand-300/80">Tráfego Pago</span>
                            <h1 className="display-2 flex items-center gap-3">Contas de anúncio <Megaphone className="h-7 w-7 text-brand-400" strokeWidth={2.2} /></h1>
                            <p className="text-ink-3 text-sm">Conecte a credencial da plataforma, escolha as contas e o Metriqa passa a guardar campanhas, anúncios e métricas diárias pro BI.</p>
                        </div>
                        <button onClick={() => setMostrarForm(v => !v)} className="btn-primary flex items-center gap-2"><KeyRound className="h-4 w-4" /> Conectar credencial</button>
                    </header>

                    {loading && <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" /></div>}

                    {!loading && (
                        <div className="space-y-6">
                            {/* KPIs */}
                            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                                {kpis.map(k => (
                                    <div key={k.label} className="surface-card surface-card-hover p-5">
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className="h-8 w-8 rounded-lg bg-brand-500/15 ring-1 ring-brand-500/25 grid place-items-center"><k.icon className="h-4 w-4 text-brand-300" /></span>
                                            <span className="label-overline">{k.label}</span>
                                        </div>
                                        <div className={`font-display font-bold tabular-nums ${k.small ? "text-lg" : "text-3xl"}`}>{k.value}</div>
                                        <p className="text-xs text-ink-4 mt-2">{k.helper}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Conectar */}
                            {mostrarForm && (
                                <form onSubmit={handleConectar} className="glow-card p-6 space-y-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <h2 className="heading-3">Conectar credencial</h2>
                                            <p className="text-xs text-ink-3 mt-1">Meta: token de <strong>Usuário do Sistema</strong> do Business Manager, sem expiração, com <code className="text-brand-300">ads_read</code>, <code className="text-brand-300">read_insights</code> e <code className="text-brand-300">business_management</code>. O token é guardado cifrado e nunca aparece na tela.</p>
                                        </div>
                                        <div className="flex gap-1 shrink-0">
                                            {PROVIDERS.map(p => (
                                                <button type="button" key={p.key} disabled={!p.disponivel} onClick={() => setForm(f => ({ ...f, provider: p.key }))}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${form.provider === p.key ? "bg-brand-500/20 border-brand-500/40 text-brand-200" : "bg-surface-2 border-white/[0.08] text-ink-3"} disabled:opacity-40`}>
                                                    {p.label}{!p.disponivel && " · em breve"}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-3">
                                        <div>
                                            <label className="text-xs text-ink-3 mb-1 block">Token</label>
                                            <textarea value={form.token} onChange={e => setForm(f => ({ ...f, token: e.target.value }))} rows={3} spellCheck={false}
                                                className="input-field w-full font-mono text-xs" placeholder="EAAG..." />
                                        </div>
                                        <div>
                                            <label className="text-xs text-ink-3 mb-1 block">ID do Business Manager (opcional)</label>
                                            <input value={form.business_id} onChange={e => setForm(f => ({ ...f, business_id: e.target.value }))} className="input-field w-full" placeholder="1077085532828054" />
                                            <p className="text-[11px] text-ink-4 mt-1">Configurações do negócio → Informações da empresa.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button type="submit" disabled={conectando} className="btn-primary">{conectando ? "Validando na Meta..." : "Validar e conectar"}</button>
                                        <button type="button" onClick={() => setMostrarForm(false)} className="btn-ghost">Cancelar</button>
                                    </div>
                                </form>
                            )}

                            {/* Credenciais */}
                            {tokens.length > 0 && (
                                <div className="surface-card p-5">
                                    <h2 className="heading-3 mb-3">Credenciais</h2>
                                    <div className="divide-y divide-white/[0.06]">
                                        {tokens.map(t => (
                                            <div key={t.id} className="flex flex-wrap items-center gap-3 py-3">
                                                <span className="chip-brand uppercase">{t.provider}</span>
                                                <div className="min-w-[220px]">
                                                    <p className="text-sm font-medium">{t.business_nome || t.business_id || "Business Manager"} <span className="text-ink-4 text-xs font-mono">…{t.token_sufixo}</span></p>
                                                    <p className="text-[11px] text-ink-4">usuário {t.usuario_nome || t.usuario_id} · {(t.scopes || []).filter(s => s !== "public_profile").join(", ")}</p>
                                                </div>
                                                <span className="text-xs text-ink-3">{t.contas_ativas}/{t.contas} contas no BI</span>
                                                <span className="text-xs text-ink-4">validado {quando(t.validado_em)}</span>
                                                {t.ultimo_erro && <span className="chip-danger" title={t.ultimo_erro}>falhou na última validação</span>}
                                                <div className="ml-auto flex gap-2">
                                                    <button onClick={() => handleRevalidar(t.id)} disabled={!!busy[`t${t.id}`]} className="btn-ghost text-xs flex items-center gap-1"><RefreshCw className={`h-3.5 w-3.5 ${busy[`t${t.id}`] ? "animate-spin" : ""}`} /> Revalidar</button>
                                                    <button onClick={() => handleRemover(t.id)} className="btn-ghost text-xs text-danger-400 flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Remover</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Contas */}
                            <div className="surface-card p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="heading-3">Contas de anúncio</h2>
                                    <span className="text-xs text-ink-4">Ligue as contas que entram no BI. O sync roda sozinho a cada 15 min pras ligadas.</span>
                                </div>
                                {contas.length === 0 && <p className="text-sm text-ink-4 py-6 text-center">Nenhuma conta ainda. Conecte uma credencial acima.</p>}
                                {contas.length > 0 && (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-white/[0.08] text-ink-3 text-left">
                                                    <th className="p-3">No BI</th>
                                                    <th className="p-3">Conta</th>
                                                    <th className="p-3">Produtos</th>
                                                    <th className="p-3">Campanhas</th>
                                                    <th className="p-3">Gasto 7d</th>
                                                    <th className="p-3">Histórico</th>
                                                    <th className="p-3">Status</th>
                                                    <th className="p-3">Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {contas.map(c => (
                                                    <tr key={c.id} className="border-b border-white/[0.06] hover:bg-surface-2/40">
                                                        <td className="p-3">
                                                            <button onClick={() => handleAtivo(c)} disabled={busy[c.id] === "ativo"} title={c.ativo ? "Tirar do BI" : "Colocar no BI"}
                                                                className={`w-11 h-6 rounded-full relative transition ${c.ativo ? "bg-positive-500/70" : "bg-surface-3"}`}>
                                                                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${c.ativo ? "left-[22px]" : "left-0.5"}`} />
                                                            </button>
                                                        </td>
                                                        <td className="p-3">
                                                            <p className="font-medium">{c.nome}</p>
                                                            <p className="text-[11px] text-ink-4 font-mono">{c.conta_id} · {c.moeda} · {c.business_nome || "—"}{c.status && c.status !== "ACTIVE" && <span className="chip-warning ml-2">{c.status}</span>}</p>
                                                        </td>
                                                        <td className="p-3 text-xs">
                                                            {(c.produtos || []).length ? c.produtos.map(p => <span key={p.produto_id} className="chip-neutral mr-1 mb-1">{p.nome}</span>) : <span className="text-ink-5">—</span>}
                                                        </td>
                                                        <td className="p-3 text-xs"><span className="text-ink-1 font-medium">{c.campanhas_ativas}</span><span className="text-ink-4"> ativas / {c.campanhas}</span></td>
                                                        <td className="p-3 text-xs tabular-nums">{Number(c.gasto_7d) > 0 ? formatR$(c.gasto_7d) : <span className="text-ink-5">—</span>}</td>
                                                        <td className="p-3 text-xs text-ink-3">{c.insights_desde ? `${dataBr(c.insights_desde)} → ${dataBr(c.insights_ate)}` : "—"}<p className="text-[10px] text-ink-5">sync {quando(c.ultimo_sync_ok)}</p></td>
                                                        <td className="p-3"><StatusConta c={c} /></td>
                                                        <td className="p-3">
                                                            <div className="flex gap-2">
                                                                <button onClick={() => handleSync(c)} disabled={!c.ativo || busy[c.id] === "sync" || c.sync_em_andamento} className="btn-ghost text-xs flex items-center gap-1 disabled:opacity-40">
                                                                    <RefreshCw className={`h-3.5 w-3.5 ${busy[c.id] === "sync" || c.sync_em_andamento ? "animate-spin" : ""}`} /> Sincronizar
                                                                </button>
                                                                <button onClick={() => handlePixels(c)} disabled={!c.ativo || busy[c.id] === "pixels"} className="btn-ghost text-xs flex items-center gap-1 disabled:opacity-40"><Database className="h-3.5 w-3.5" /> Pixels</button>
                                                            </div>
                                                            {c.ultimo_erro && <p className="text-[10px] text-danger-400 mt-1 max-w-[260px] truncate" title={c.ultimo_erro}>{c.ultimo_erro}</p>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {pixels && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={e => { if (e.target === e.currentTarget) setPixels(null); }}>
                    <div className="surface-card w-full max-w-2xl my-4 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div><h2 className="heading-3">Pixels da conta</h2><p className="text-xs text-ink-4">{pixels.conta.nome} · use o ID em Tracking → Pixels & CAPI por produto</p></div>
                            <button onClick={() => setPixels(null)} className="text-ink-3 hover:text-white text-xl leading-none">✕</button>
                        </div>
                        <div className="divide-y divide-white/[0.06] max-h-[60vh] overflow-y-auto">
                            {pixels.lista.map(p => (
                                <div key={p.pixel_id} className="flex items-center gap-3 py-2 text-sm">
                                    <span className="font-mono text-xs text-brand-300">{p.pixel_id}</span>
                                    <span className="flex-1 truncate">{p.nome}</span>
                                    <span className="text-[11px] text-ink-4">{p.ultimo_disparo ? `disparou ${quando(p.ultimo_disparo)}` : "sem disparo"}</span>
                                    <button onClick={() => { navigator.clipboard.writeText(p.pixel_id); toast.success("ID copiado"); }} className="btn-ghost text-xs">Copiar</button>
                                </div>
                            ))}
                            {pixels.lista.length === 0 && <p className="text-sm text-ink-4 py-4 text-center">Nenhum pixel nesta conta.</p>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
